import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Sidebar } from "../components/Sidebar";
import { Header } from "../components/Header";
import HighPerformanceAd from "../components/HighPerformanceAd";
import { Footer } from "../components/ Footer";
import axios from "../axiosInstance";

function Toast({ message, type = "error", onClose }) {
  return (
    <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded shadow-lg text-white ${type === "error" ? "bg-red-600" : "bg-green-600"}`}>
      <div className="flex items-center space-x-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-300 font-bold">×</button>
      </div>
    </div>
  );
}

export default function AccountSettings({ onLogout }) {
  const [coinz, setCoinz] = useState(0);
  const [options, setOptions] = useState(0);
  const [avgOptionPrice, setAvgOptionPrice] = useState(0);
  const [selfWeeklyProfit, setSelfWeeklyProfit] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [wallets, setWallets] = useState({ btc: "", eth: "", sol: "", waves: "" });
  const [toast, setToast] = useState({ message: "", type: "" });
  const [stats, setStats] = useState(null);
  
  const showToast = (message, type = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const getUserData = () => {
    axios.get(`/api/user/me`, { withCredentials: true })
      .then((response) => {
        const { coinz, options, systemCoinz, weeklyProfit, email, username, wallets } = response.data;
        setSelfWeeklyProfit(options > 0 ? ((systemCoinz / weeklyProfit) * options) : 0);
        setCoinz(coinz);
        setOptions(options);
        setEmail(email);
        setUsername(username);
        setWallets(wallets || { BTC: "", ETH: "", SOL: "", WAVES: "" });
      })
      .catch((error) => {
        console.error("Error retrieving user data:", error);
      });
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`/api/user/stats`, { withCredentials: true });
      const data = res.data;
      console.log(data);
      setStats(data);
    } catch (err) {
      console.error("Erreur fetchStats:", err);
      setError(err.message);
    }
  };

  const activityData = [
    {
      type: "Draws",
      totalCount: stats?.draws_count ?? 0,
      totalCoinz: stats?.draws_total ?? 0,
    },
    {
      type: "Autosurf",
      totalCount: stats?.autosurf_visits_count ?? 0,
      totalCoinz: stats?.autosurf_total ?? 0,
    },
    {
      type: "Referral",
      totalCount:0,
      totalCoinz: stats?.referral_total ?? 0,
    },
  ];

  const getOptionAvg = async () => {
    try {
      const res = await axios.get(`/api/transactions/last-sells`);
      const prices = res.data.lastOptionSellPrices;
      if (prices.length) {
        const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
        setAvgOptionPrice(avg);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveChanges = async () => {
    try {
      await axios.post(`/api/user/update-profile`, {
        email,
        newPassword: password || undefined,
        wallets
      }, { withCredentials: true });
  
      showToast("Changes saved. Please confirm via email.", "success");
    } catch (err) {
      console.error(err);
      showToast("Error updating profile.", "error");
    }
  };

  useEffect(() => {
    document.title = "User Settings | CoinzUp";
    getUserData();
    getOptionAvg();
    fetchStats();
  }, []);

  const coinzUSD = coinz / 100000;
  const optsUSD = options * (avgOptionPrice / 100000);
  const totalValeur = (coinzUSD + optsUSD).toFixed(4);

  return (
    <>
      {toast.message && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "" })} />}

      <div className="min-h-screen flex flex-col bg-gray-900 text-white">
        <div className="flex flex-1">
          <Sidebar onLogout={onLogout} />
          <main className="flex-1 p-8 space-y-10 overflow-auto">
            <Header title="Profile" balance={coinz} optionsOwned={options} estimatedWeeklyGain={selfWeeklyProfit} />

            <HighPerformanceAd
          keyId="fd5da3543c594f07b73630f461f10613"
          width={728}
          height={90}
        />

            <div className="max-w-3xl mx-auto bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
              <h2 className="text-xl font-semibold text-cyan-400">Profil Valorisation</h2>
              <p>Total Estimated Value: <strong>{totalValeur} USD</strong></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700 p-4 rounded shadow">
                  <h3 className="font-medium">Coinz</h3>
                  <p>{coinz} → {coinzUSD.toFixed(4)} USD</p>
                </div>
                <div className="bg-gray-700 p-4 rounded shadow">
                  <h3 className="font-medium">Options</h3>
                  <p>{options} × {avgOptionPrice.toFixed(4)} COINZ = {optsUSD.toFixed(4)} USD</p>
                  <p className="text-xs text-gray-400">Avg last price: {avgOptionPrice.toFixed(4)} Coinz ({avgOptionPrice.toFixed(4) / 100000} USD) </p>
                </div>
              </div>
              <p className="text-sm text-gray-300 pt-2">
                💡 <strong>Increase your valuation</strong> by 
                <a href="/marketplace" className="text-cyan-400 hover:underline px-1">purchasing options</a> 
                on the marketplace or through the 
                <a href="/referral" className="text-cyan-400 hover:underline px-1">referral program</a>. 
                You can also 
                <a href="/howitworks" className="text-cyan-400 hover:underline px-1">learn how to earn more Coinz</a> by reading our FAQ.
              </p>
            </div>

            <div className="max-w-3xl mx-auto bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
              <h2 className="text-xl font-semibold text-cyan-400">Activity Stats</h2>
              <table className="min-w-full table-auto border-collapse border border-gray-700">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="py-3 px-5 border border-gray-700 text-left text-sm font-medium text-gray-100">Activity Type</th>
                    <th className="py-3 px-5 border border-gray-700 text-right text-sm font-medium text-gray-100">Count</th>
                    <th className="py-3 px-5 border border-gray-700 text-left text-sm font-medium text-gray-100">Total Coinz</th>
                  </tr>
                </thead>
                <tbody>
                  {activityData.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="py-4 px-5 text-center text-gray-500 text-sm font-normal">
                        No activity data available
                      </td>
                    </tr>
                  ) : (
                    activityData.map(({ type, totalCoinz, totalCount }, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-gray-900" : "bg-gray-800"}
                      >
                        <td className="py-3 px-5 text-sm">{type}</td>
                        <td className="py-3 px-5 text-right text-sm font-mono">{totalCount}</td>
                        <td className="py-3 px-5 text-sm">{Number(totalCoinz).toFixed(4)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              <Card className="bg-gray-800 border border-gray-700">
                <CardContent className="p-6 space-y-6">
                  <h3 className="text-lg font-semibold">Account Details</h3>

                  <p><strong>Username:</strong> {username}</p>

                  <label className="block text-sm font-medium text-gray-300">Email Address</label>
                  <input type="email" className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600" value={email} onChange={e => setEmail(e.target.value)} />

                  <label className="block text-sm font-medium text-gray-300">New Password</label>
                  <input type="password" className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600" value={password} onChange={e => setPassword(e.target.value)} />

                  <label className="block text-sm font-medium text-gray-300">Confirm Password</label>
                  <input type="password" className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />

                  <h3 className="text-lg font-semibold pt-4">Wallet Addresses</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(wallets).map(([currency, address]) => (
                    <div key={currency}>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        {currency.toUpperCase()} Wallet
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600"
                        value={address}
                        onChange={(e) => setWallets((prev) => ({ ...prev, [currency]: e.target.value }))}
                      />
                    </div>
                  ))}
                  </div>

                  <Button onClick={handleSaveChanges} className="w-full bg-blue-600 hover:bg-blue-700 mt-4">
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </div>

            <HighPerformanceAd
          keyId="96e99453a24be0353f2fe26dc0b9b728"
          width={468}
          height={60}
        />
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}
